import { useEffect, useLayoutEffect } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

// Take over scroll restoration from the browser so PUSH navigations
// always start at the top, even on mobile browsers that try to
// restore the previous scroll offset after the new page renders.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

const scrollTop = () => window.scrollTo({ top: 0, left: 0, behavior: "auto" });

const positions = new Map<string, number>();

/**
 * Resets scroll position to the top on client-side navigations.
 * Because we manage scroll restoration manually, back/forward (POP)
 * navigations restore the offset we saved for that history entry.
 * In-page hash links are left untouched.
 */
export function ScrollToTop() {
  const { pathname, hash, key } = useLocation();
  const navigationType = useNavigationType();

  // Save the outgoing entry's offset synchronously during commit, BEFORE the
  // scroll-reset layout effect below runs. Declared first so its cleanup fires
  // first. A passive effect would run too late (after window.scrollTo(0)).
  useLayoutEffect(() => {
    const currentKey = key;
    return () => {
      positions.set(currentKey, window.scrollY);
    };
  }, [key]);

  useLayoutEffect(() => {
    if (hash) return;
    if (navigationType === "POP") {
      window.scrollTo({ top: positions.get(key) ?? 0, left: 0, behavior: "auto" });
      return;
    }
    scrollTop();
  }, [pathname, hash, navigationType, key]);


  useEffect(() => {
    if (hash) return;
    if (navigationType === "POP") {
      const saved = positions.get(key);
      if (saved === undefined) return;
      const restore = () => window.scrollTo({ top: saved, left: 0, behavior: "auto" });
      const raf = requestAnimationFrame(restore);
      const timeout = window.setTimeout(restore, 120);
      return () => {
        cancelAnimationFrame(raf);
        window.clearTimeout(timeout);
      };
    }
    const raf = requestAnimationFrame(scrollTop);
    const timeout = window.setTimeout(scrollTop, 120);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timeout);
    };
  }, [pathname, hash, navigationType, key]);

  return null;
}

export default ScrollToTop;
